import { service } from '@ember/service'
import Component from '@glimmer/component'
import { format } from 'date-fns'
import { colors } from '@colors'
import { t, type IntlService } from 'ember-intl'
import type { AxisLabelsFormatterContextObject, Point } from 'highcharts'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import { array } from '@ember/helper'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import type { AssessmentScoresStudentData } from 'teacher-ui-client/utils/student-events/fetchers/assessment-scores-student'

interface Args {
  data: AssessmentScoresStudentData
  summaryTitle: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class AssessmentScoresStudent extends Component<Signature> {
  @service intl!: IntlService

  readonly colorScheme: SummaryBoxColorScheme = SummaryBoxColorScheme.Orange

  yAxisLabelFormatter(this: AxisLabelsFormatterContextObject): string {
    return `${this.value}%`
  }

  dataLabelFormatter(this: Point): string {
    return this.name
  }

  get chartConfig() {
    const scoreEvents = this.args.data.assessment_scores_events ?? []

    const scoreItems = scoreEvents.map((scoreEvent, index) => {
      const dataLabel = `${scoreEvent.score}%`
      return { x: index, y: scoreEvent.score, name: dataLabel, event: scoreEvent }
    })

    const zones = [
      { value: 50, color: colors.watermelonyRed300 },
      { value: 80, color: colors.juicyOrange300 },
      { color: colors.msGreen300 },
    ]
    const data: SeriesColumnOptions[] = [{ data: scoreItems, zones, type: 'column' }]
    const mapNumbers = scoreEvents.map((item) => String(item.map_number))

    return { scoreEvents, data, mapNumbers }
  }

  get summaryStats(): Stat[] {
    const { intl } = this
    const { average_score, total_count } = this.args.data

    const averageLabel = intl.t('reporting.statBoxes.assessmentScoresStudent.average')
    const totalLabel = intl.t('reporting.statBoxes.assessmentScoresTotals.totals.re')
    const averageScore = formatValue(average_score, { append: '%' })
    const totalQuizzesTaken = formatValue(total_count)

    return [
      { label: averageLabel, value: averageScore },
      { label: totalLabel, value: totalQuizzesTaken },
    ]
  }

  get toolTipFormatter() {
    const { intl } = this
    const dateFormat = intl.t('dateFormats.dateFns.dd/MM')

    return function (this: Point) {
      const { event } = this as Point & { event: { created_at: number } }
      const timestamp = event.created_at
      const formattedDate = format(new Date(timestamp * 1000), dateFormat)
      return intl.t('reporting.tooltips.assessmentScoresStudent.date', { formattedDate })
    }
  }

  <template>
    <div data-test-assessment-scores-student class="relative h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @stats={{this.summaryStats}}
            @colorScheme={{this.colorScheme}}
          />
        </:left>
        <:right>
          <div class="flex h-full">
            {{#if this.chartConfig.scoreEvents}}
              <SeriesColumn
                class="shrink grow"
                @data={{this.chartConfig.data}}
                @categories={{this.chartConfig.mapNumbers}}
                @colorByPoint={{true}}
                @toolTipFormatter={{this.toolTipFormatter}}
                @xAxisLabel={{t "reporting.xAxisLabels.assessmentScoresStudent"}}
                @yAxisLabel={{t "reporting.yAxisLabels.assessmentScoresStudent"}}
                @yRange={{array 0 100}}
                @yAxisLabelFormatter={{this.yAxisLabelFormatter}}
                @yAxisTickInterval={{25}}
                @dataLabelsEnabled={{true}}
                @dataLabelsFormatter={{this.dataLabelFormatter}}
              />
            {{/if}}
          </div>
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default AssessmentScoresStudent
